import type { ReactNode } from 'react';
import { WEB_BASE_URL } from '../web-base-url';
import { openInBrowser } from './tester/harness';
import { ExternalLinkIcon } from '../ExternalLinkIcon';

interface Props {
  /**
   * Relative web path the button hands off to — built via
   * `webRoutes.*.build()` at the call-site so route changes propagate
   * through TypeScript. The component joins it against `WEB_BASE_URL` so
   * preview / staging / prod all resolve correctly.
   */
  readonly path: string;
  /**
   * Extra class names for layout-specific styling (e.g. anchoring the button
   * to a section header). The base `web-popout-button` class always wins for
   * the affordance treatment — color, icon spacing, hover state — so every
   * handoff in the Activity reads the same regardless of where it's mounted.
   */
  readonly className?: string;
  readonly children: ReactNode;
}

/**
 * Single source of truth for "leaves the Activity iframe" buttons (#4105).
 *
 * Every Activity → web handoff goes through this component so the visual
 * treatment (label + trailing `<ExternalLinkIcon />`) and the mechanism
 * (`openInBrowser` → SDK `openExternalLink`) are uniform. Adding a new
 * popout becomes one import + one JSX node instead of three things to
 * remember (URL join, openInBrowser call, icon placement) duplicated at
 * every call-site.
 *
 * The component is intentionally generic — it doesn't know or care whether
 * the destination is `/admin/*`, the tester schedule, or account settings.
 * Discrimination of which handoffs are worth surfacing belongs to the
 * survey in the issue body, not to the component contract.
 */
export function WebPopoutButton({ path, className, children }: Props) {
  const url = new URL(path, WEB_BASE_URL).toString();
  const classes = className
    ? `web-popout-button ${className}`
    : 'web-popout-button';

  return (
    <button
      type="button"
      className={classes}
      onClick={() => void openInBrowser(url)}
    >
      {children}
      <ExternalLinkIcon />
    </button>
  );
}
